import React from 'react';
import { getStatusStyle } from '@data/statusConfig';

const FRAME_LABELS = {
  classic: 'קלאסי',
  bold: 'נועז',
  ever: 'נקי',
  clean: 'אותנטי',
  village: 'כפרי',
  nordic: 'נורדי',
};

const FRAME_MAP = {
  classic: { bg: '/assets/file/images/frame_classic.png', left: '16.5%', top: '16.2%', width: '65.4%', height: '66.5%' },
  bold:    { bg: '/assets/file/images/frame_bold.png',    left: '3.8%',  top: '3.8%',  width: '90.4%', height: '91%' },
  ever:    { bg: '/assets/file/images/frame_even.png',    left: '3.5%',  top: '3.5%',  width: '90.8%', height: '91%' },
  clean:   { bg: '/assets/file/images/frame_clean.png',   left: '13.8%', top: '14.2%', width: '70.4%', height: '70%' },
  nordic:  { bg: '/assets/file/images/frame_nordic.png',  left: '3.8%',  top: '3.8%',  width: '90.4%', height: '91%' },
  village: { bg: '/assets/file/images/frame_village.png', left: '16.5%', top: '16.2%', width: '65.4%', height: '66.5%' },
};

// Pull a 4-digit year out of any date string format we render. The API returns
// pre-formatted Hebrew dates ("15 באוקטובר 2026") alongside numeric forms
// (15.10.2026), so a regex is the safest extractor across both.
const getOrderYear = (order) => {
  const m = String(order.date || '').match(/\b(20\d{2})\b/);
  if (m) return parseInt(m[1], 10);
  return new Date().getFullYear();
};

const groupOrdersByYear = (orders) => {
  const map = new Map();
  for (const order of orders) {
    const year = getOrderYear(order);
    if (!map.has(year)) map.set(year, []);
    map.get(year).push(order);
  }
  return [...map.entries()].sort((a, b) => b[0] - a[0]);
};

const summarizeYear = (yearOrders) => {
  const ordersCount = yearOrders.length;
  const printsCount = yearOrders.reduce(
    (s, o) => s + (parseInt(o.items, 10) || 0),
    0,
  );
  return { ordersCount, printsCount };
};

const OrderCard = ({ order, onClick }) => {
  const statusStyle = getStatusStyle(order.statusTone);
  const frame = FRAME_MAP[order.frameType];
  const itemsCount = parseInt(order.items, 10) || 0;
  const visibleThumbs = (order.thumbnails || []).slice(0, 3);
  const moreCount = Math.max(0, itemsCount - visibleThumbs.length);

  const handleActivate = () => onClick(order.orderId);

  return (
    <div
      className="pa-order-card"
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      <div className="pa-order-card__header">
        <div className="pa-order-card__info">
          <span className="pa-order-card__id">הזמנה #{order.orderId}</span>
          <span className="pa-order-card__date">{order.date}</span>
        </div>
        <span
          className="pa-order-card__status"
          style={{
            backgroundColor: statusStyle.backgroundColor,
            color: statusStyle.color,
            textShadow: statusStyle.textShadow || 'none',
          }}
        >
          {order.displayStatus || order.status}
        </span>
      </div>

      {visibleThumbs.length > 0 && (
        <div className="pa-order-card__thumbs">
          {visibleThumbs.map((src, i) => (
            frame ? (
              <div key={i} className="pa-order-card__thumb pa-order-card__thumb--framed">
                <div
                  className="pa-order-card__frame-wrap"
                  style={{ backgroundImage: `url(${frame.bg})` }}
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="pa-order-card__frame-photo"
                    style={{
                      left: frame.left,
                      top: frame.top,
                      width: frame.width,
                      height: frame.height,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div key={i} className="pa-order-card__thumb">
                <img src={src} alt="" loading="lazy" />
              </div>
            )
          ))}
          {moreCount > 0 && (
            <div className="pa-order-card__thumb pa-order-card__thumb--more">
              +{moreCount}
            </div>
          )}
        </div>
      )}

      <div className="pa-order-card__details">
        <div className="pa-order-card__meta">
          <span><strong>{FRAME_LABELS[order.frameType] || order.frameType}</strong></span>
          <span>·</span>
          <span>{itemsCount} תמונות</span>
        </div>
        {order.price && (
          <span className="pa-order-card__price">
            {order.price}<span className="pa-cur">&#8362;</span>
          </span>
        )}
      </div>

      {order.creditsEarned > 0 && (
        <div className="pa-order-card__credits">
          <span className="pa-order-card__credits-text">
            צברת <strong>&#8362;{order.creditsEarned.toFixed(2)}</strong> {'‎'}Moments+{'‎'} מהזמנה זו
          </span>
        </div>
      )}
    </div>
  );
};

const DATE_FILTERS = [
  { value: 90, label: '3 חודשים' },
  { value: 180, label: 'חצי שנה' },
  { value: 2025, label: '2025' },
  { value: 2024, label: '2024' },
];

const OrderList = ({ orders, loading, error, onRetry, onSelectOrder, dateFilter, onDateFilterChange }) => {
  if (loading) {
    return (
      <div className="pa-orders">
        <div className="pa-orders__header">
          <h2 className="pa-orders__title">ההזמנות שלי</h2>
        </div>
        {dateFilter !== undefined && (
          <div className="pa-date-filters">
            {DATE_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`pa-date-filters__btn${dateFilter === f.value ? ' pa-date-filters__btn--active' : ''}`}
                disabled
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
        <div className="pa-orders__loading">
          {[1, 2, 3].map((i) => (
            <div key={i} className="pa-order-card pa-order-card--skeleton">
              {/* Header — id + date on the right, status pill on the left */}
              <div className="pa-order-card__header">
                <div className="pa-order-card__info">
                  <div className="skeleton-line" style={{ width: 130, height: 17 }} />
                  <div className="skeleton-line" style={{ width: 90, height: 11, marginTop: 6 }} />
                </div>
                <div className="skeleton-line" style={{ width: 64, height: 24, borderRadius: 999 }} />
              </div>

              {/* Overlapping framed-thumb cluster */}
              <div className="pa-order-card__thumbs">
                <div className="pa-order-card__thumb pa-order-card__thumb--skel" />
                <div className="pa-order-card__thumb pa-order-card__thumb--skel" />
                <div className="pa-order-card__thumb pa-order-card__thumb--skel" />
              </div>

              {/* Details — meta on the right, price on the left */}
              <div className="pa-order-card__details">
                <div className="skeleton-line" style={{ width: 110, height: 12 }} />
                <div className="skeleton-line" style={{ width: 58, height: 20 }} />
              </div>

              {/* Credits strip */}
              <div className="skeleton-line" style={{ width: '100%', height: 30, borderRadius: 8, marginTop: 12 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const yearGroups = groupOrdersByYear(orders);

  return (
    <div className="pa-orders">
      <div className="pa-orders__header">
        <h2 className="pa-orders__title">ההזמנות שלי</h2>
      </div>
      

      {dateFilter !== undefined && (
        <div className="pa-date-filters">
          {DATE_FILTERS.map((f) => (
            <button
              key={f.value}
              className={`pa-date-filters__btn${dateFilter === f.value ? ' pa-date-filters__btn--active' : ''}`}
              onClick={() => onDateFilterChange(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {error ? (
        <div className="pa-orders__empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p>{error}</p>
          {onRetry && (
            <button className="pa-retry-btn" onClick={onRetry}>נסו שוב</button>
          )}
        </div>
      ) : orders.length === 0 ? (
        <div className="pa-orders__empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#c8c8c8" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
          <p>לא נמצאו הזמנות בטווח זה</p>
        </div>
      ) : (
        yearGroups.map(([year, yearOrders]) => {
          const summary = summarizeYear(yearOrders);
          return (
            <section key={year} className="pa-year-section">
              <div className="pa-year-head">
                <h3 className="pa-year-title">קיר <em>{year}</em></h3>
                <span className="pa-year-summary">
                  <strong>{summary.ordersCount} הזמנות</strong> · {summary.printsCount} בלנדס
                </span>
              </div>
              <div className="pa-orders__grid">
                {yearOrders.map((order) => (
                  <OrderCard
                    key={order.orderId}
                    order={order}
                    onClick={onSelectOrder}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
};

export default OrderList;
